import {
    AccountCategory,
    type AccountSummary,
    type CategorySummary,
    type HolisticSummary,
    type HolisticSummaryCategory,
    LinkState,
    type MoneyMapWithRulesAndAccounts,
    SpendAccountCategory,
} from '@fruitful/types';
import { orderBy } from 'lodash';

import {
    type BankAccountDisplayDetail,
    FRUITFUL_INSTITUTION_NAME,
} from '../../banking/types/BankAccount';
import {
    type AccountGroup,
    type AccountGroupCategory,
    type AccountGroups,
    FruitfulAccountCategory,
    type GroupedAccount,
    type GroupedAccountId,
} from '../../holistic/types/HolisticSummary';
import { centsToDollars, sortBankAccounts } from './bankingUtils';

export const isAccountInErrorState = ({
    linkState,
    needsRecategorization,
}: AccountSummary): boolean =>
    linkState === LinkState.ERROR || !!needsRecategorization;

export const isUnknownAccountType = (accountType?: string | null): boolean => {
    return accountType == null;
};

// Manual accounts use 'cash' or 'credit'
export const isLiabilityAccountType = (accountType?: string): boolean =>
    (accountType ?? '').toLowerCase().includes('liability') ||
    (accountType ?? '').toLowerCase().includes('loan') ||
    (accountType ?? '').toLowerCase() === 'credit';

// Sort accounts within a given group into a display order
// Also assign an id to each account within its group for reference in the UI
const sortGroupAccounts = (
    groupId: string,
    accounts: AccountSummary[],
): GroupedAccount[] => {
    const initialSortedAccounts = accounts.sort((a, b) => {
        if (
            a.institutionName === FRUITFUL_INSTITUTION_NAME &&
            b.institutionName !== FRUITFUL_INSTITUTION_NAME
        ) {
            return -1;
        }
        if (
            a.institutionName !== FRUITFUL_INSTITUTION_NAME &&
            b.institutionName === FRUITFUL_INSTITUTION_NAME
        ) {
            return 1;
        }
        return 0;
    });

    const sortedAccounts = orderBy(
        initialSortedAccounts,
        ['institutionName', 'currentBalanceCents'],
        ['asc', 'desc'],
    );

    return sortedAccounts.map((account, index) => ({
        ...account,
        groupedAccountId: `${groupId}-${index}`,
    }));
};

const sortFruitfulAccounts = (
    groupId: string,
    accounts: AccountSummary[],
    moneyMap?: MoneyMapWithRulesAndAccounts,
    bankAccountDetails?: BankAccountDisplayDetail[],
) => {
    const holisticBankAccountIds = accounts.map((a) => a.bankAccountId);
    const holisticBankAccountDetails =
        bankAccountDetails?.filter((d) =>
            holisticBankAccountIds.includes(d.bankAccountId),
        ) ?? [];
    const sortedHolisticBankAccountDetails = sortBankAccounts(
        holisticBankAccountDetails,
        moneyMap,
    );

    const syncteraAccounts = sortedHolisticBankAccountDetails.reduce<
        AccountSummary[]
    >((acc, bankAccount) => {
        const account = accounts.find(
            (a) => a.bankAccountId === bankAccount.bankAccountId,
        );

        if (account != null) {
            acc.push(account);
        }
        return acc;
    }, []);

    const nonSyncteraAccounts = accounts.reduce<AccountSummary[]>(
        (acc: AccountSummary[], bankAccount: AccountSummary) => {
            const isFFAccount =
                syncteraAccounts.find(
                    (ffa) => ffa.bankAccountId === bankAccount.bankAccountId,
                ) != null;
            if (!isFFAccount) {
                acc.push(bankAccount);
            }
            return acc;
        },
        [],
    );

    return [...syncteraAccounts, ...nonSyncteraAccounts].map(
        (account, index) => ({
            ...account,
            groupedAccountId: `${groupId}-${index}`,
        }),
    );
};

// Given a category of accounts, group them into one or more dictionary entries
// The groupings allow us to split a category like "Spend" into "Cash" and "Credit"
export const groupAccounts = (
    category: AccountCategory,
    accounts: AccountSummary[],
    moneyMap?: MoneyMapWithRulesAndAccounts,
    bankAccountDetails?: BankAccountDisplayDetail[],
): AccountGroups => {
    const fruitfulAccounts = accounts.filter(
        (a) => a.institutionName === FRUITFUL_INSTITUTION_NAME,
    );
    const nonFruitfulAccounts = accounts.filter(
        (a) => a.institutionName !== FRUITFUL_INSTITUTION_NAME,
    );
    const fruitfulAccountsGroup = {
        accountGroupCategory: FruitfulAccountCategory.FRUITFUL,
        heading: 'Fruitful',
        accounts: sortFruitfulAccounts(
            FruitfulAccountCategory.FRUITFUL,
            fruitfulAccounts,
            moneyMap,
            bankAccountDetails,
        ),
    } satisfies AccountGroup;

    if (category === AccountCategory.SPEND) {
        const creditAccounts = nonFruitfulAccounts.filter((a) =>
            isLiabilityAccountType(a.type ?? ''),
        );
        const cashAccounts = nonFruitfulAccounts.filter(
            (a) => !isLiabilityAccountType(a.type ?? ''),
        );

        return {
            ...(fruitfulAccounts.length > 0 && {
                [FruitfulAccountCategory.FRUITFUL]: fruitfulAccountsGroup,
            }),
            [SpendAccountCategory.CASH]: {
                accountGroupCategory: SpendAccountCategory.CASH,
                heading: 'Cash',
                accounts: sortGroupAccounts(
                    SpendAccountCategory.CASH,
                    cashAccounts,
                ),
            },
            [SpendAccountCategory.CREDIT]: {
                accountGroupCategory: SpendAccountCategory.CREDIT,
                heading: 'Credit',
                accounts: sortGroupAccounts(
                    SpendAccountCategory.CREDIT,
                    creditAccounts,
                ),
            },
        };
    }

    return {
        ...(fruitfulAccounts.length > 0 && {
            [FruitfulAccountCategory.FRUITFUL]: fruitfulAccountsGroup,
        }),
        [category]: {
            accountGroupCategory: category,
            heading: 'Linked accounts',
            accounts: sortGroupAccounts(category, nonFruitfulAccounts),
        },
    };
};

// In groupAccounts(), accounts within a category are grouped and then
// sorted by sortGroupAccounts(). This function creates an id for each
// account within its group. The function findAccountByGroupedAccountId here
// is used to find an account by its groupedAccountId so we can get all data
// for the right account from this id.
export const findAccountByGroupedAccountId = (
    category: HolisticSummaryCategory,
    categoryAccounts: AccountSummary[],
    groupedAccountId: GroupedAccountId,
) => {
    const accountGroups = groupAccounts(category, categoryAccounts);
    const groupAccountIdParts = groupedAccountId.split('-');
    const groupId = groupAccountIdParts[0];
    const accountGroup =
        accountGroups[(groupId ?? category) as AccountGroupCategory];

    if (accountGroup) {
        return accountGroup.accounts.find((account) => {
            return account.groupedAccountId === groupedAccountId;
        });
    }

    return undefined;
};

export const getHolisticAssetsTotalDollars = (
    holisticSummary: HolisticSummary | null | undefined,
): number | null => {
    if (!holisticSummary) return null;

    const categories = [
        AccountCategory.SPEND,
        AccountCategory.SAVE,
        AccountCategory.INVEST,
    ];

    let hasNonNullValue = false;

    const assetsTotalCents = categories.reduce((acc, category) => {
        const categorySummary = holisticSummary?.[category] as CategorySummary;
        const balanceCents = categorySummary?.totalCurrentBalanceCents;

        if (balanceCents !== null && balanceCents !== undefined) {
            hasNonNullValue = true;
            return acc + balanceCents;
        }

        return acc;
    }, 0);

    return hasNonNullValue ? centsToDollars(assetsTotalCents) : null;
};
